import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import {
  Printer, FileText, Upload, CheckCircle, Loader2, 
  Clock, Shield, Download
} from "lucide-react";
import MainLayout from "../components/MainLayout";
import SEO, { pageSEO } from "../components/SEO";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PrintingServicesPage = () => {
  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState("");
  const [pages, setPages] = useState(1);
  const [copies, setCopies] = useState(1);
  const [colorMode, setColorMode] = useState("bw"); // "bw" or "color"
  const [sides, setSides] = useState("single"); // "single" or "double"
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [notes, setNotes] = useState("");
  const [deliveryOption, setDeliveryOption] = useState("pickup"); // "pickup" or "delivery"
  const [deliveryAddress, setDeliveryAddress] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Pricing in pence per page
  const prices = {
    bw: { single: 20, double: 30 },
    color: { single: 40, double: 50 }
  };
  const deliveryFee = 200; // £2 in pence

  const pricePerSheet = prices[colorMode][sides];
  // For double-sided: 2 pages = 1 sheet, so sheets = ceil(pages / 2)
  // For single-sided: 1 page = 1 sheet
  const sheetsPerCopy = sides === "double" ? Math.ceil(pages / 2) : pages;
  const totalSheets = sheetsPerCopy * copies;
  const printingTotal = pricePerSheet * totalSheets;
  const deliveryCost = deliveryOption === "delivery" ? deliveryFee : 0;
  const totalPrice = ((printingTotal + deliveryCost) / 100).toFixed(2);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      setFileName(selectedFile.name);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file || !name || !email) {
      toast.error("Please fill in all required fields");
      return;
    }
    if (deliveryOption === "delivery" && !deliveryAddress) {
      toast.error("Please enter a delivery address");
      return;
    }

    setSubmitting(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('name', name);
      formData.append('email', email);
      formData.append('phone', phone);
      formData.append('pages', pages);
      formData.append('copies', copies);
      formData.append('color_mode', colorMode);
      formData.append('sides', sides);
      formData.append('notes', notes);
      formData.append('delivery_option', deliveryOption);
      formData.append('delivery_address', deliveryAddress);
      formData.append('price_per_sheet', pricePerSheet);
      formData.append('total_price', totalPrice);

      await axios.post(`${API}/printing/order`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setSubmitted(true);
      toast.success("Print order submitted successfully!");
    } catch (err) {
      toast.error("Failed to submit order. Please try again.");
    }
    setSubmitting(false);
  };

  if (submitted) {
    return (
      <MainLayout>
        <SEO {...pageSEO.printingServices} />
        <section className="pt-12 pb-16">
          <div className="max-w-2xl mx-auto px-4 text-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="card p-12"
            >
              <div className="w-20 h-20 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-green-500" />
              </div>
              <h2 className="text-3xl font-bold text-white mb-4">Order Submitted!</h2>
              <p className="text-slate-400 mb-6">
                Thank you for your print order. We'll contact you shortly to confirm the details and arrange collection or delivery.
              </p>
              <div className="bg-slate-800 rounded-lg p-4 mb-6 text-left">
                <p className="text-slate-300"><strong>Document:</strong> {fileName}</p>
                <p className="text-slate-300"><strong>Pages:</strong> {pages}</p>
                <p className="text-slate-300"><strong>Copies:</strong> {copies}</p>
                <p className="text-slate-300"><strong>Total Sheets:</strong> {totalSheets}</p>
                <p className="text-slate-300"><strong>Type:</strong> {colorMode === "bw" ? "Black & White" : "Colour"}, {sides === "single" ? "Single-sided" : "Double-sided"}</p>
                <p className="text-slate-300"><strong>Collection:</strong> {deliveryOption === "pickup" ? "Pick up (Free)" : `Delivery (+£2)`}</p>
                {deliveryOption === "delivery" && deliveryAddress && (
                  <p className="text-slate-300"><strong>Address:</strong> {deliveryAddress}</p>
                )}
                <p className="text-slate-300"><strong>Total:</strong> £{totalPrice}</p>
              </div>
              <Link to="/" className="btn-primary inline-flex">
                Back to Home
              </Link>
            </motion.div>
          </div>
        </section>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <SEO {...pageSEO.printingServices} />
      
      {/* Hero Section */}
      <section className="pt-12 pb-12 gradient-hero gradient-radial">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-3xl mx-auto"
          >
            <div className="mb-6 flex justify-center">
              <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-blue-700 rounded-2xl flex items-center justify-center shadow-[0_0_40px_rgba(59,130,246,0.4)]">
                <Printer className="w-10 h-10 text-white" />
              </div>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Printing Services
            </h1>
            <p className="text-lg text-slate-300 mb-2">
              Fast, affordable A4 printing
            </p>
            <p className="text-slate-400">
              Upload your document and we'll have it ready for collection
            </p>
          </motion.div>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="py-8 bg-slate-950">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-xl font-bold text-white text-center mb-6">Our Prices (A4)</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="card p-4 text-center">
              <div className="text-2xl font-bold text-white mb-1">20p</div>
              <p className="text-slate-400 text-sm">B&W Single</p>
            </div>
            <div className="card p-4 text-center">
              <div className="text-2xl font-bold text-white mb-1">30p</div>
              <p className="text-slate-400 text-sm">B&W Double</p>
            </div>
            <div className="card p-4 text-center">
              <div className="text-2xl font-bold text-blue-400 mb-1">40p</div>
              <p className="text-slate-400 text-sm">Colour Single</p>
            </div>
            <div className="card p-4 text-center">
              <div className="text-2xl font-bold text-blue-400 mb-1">50p</div>
              <p className="text-slate-400 text-sm">Colour Double</p>
            </div>
          </div>
        </div>
      </section>

      {/* Order Form */}
      <section className="py-8 bg-slate-900/50">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <form onSubmit={handleSubmit} className="card p-6 md:p-8" data-testid="print-form">
            <h2 className="text-xl font-bold text-white mb-6">Submit Print Order</h2>
            
            {/* File Upload */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Upload Document *
              </label>
              <div className="border-2 border-dashed border-slate-700 rounded-lg p-6 text-center hover:border-blue-500 transition-colors">
                <input
                  type="file"
                  id="file-upload"
                  className="hidden"
                  onChange={handleFileChange}
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.jpg,.jpeg,.png"
                />
                <label htmlFor="file-upload" className="cursor-pointer">
                  {fileName ? (
                    <div className="flex items-center justify-center gap-3">
                      <FileText className="w-8 h-8 text-blue-500" />
                      <div className="text-left">
                        <p className="text-white font-medium">{fileName}</p>
                        <p className="text-slate-400 text-sm">Click to change file</p>
                      </div>
                    </div>
                  ) : (
                    <>
                      <Upload className="w-12 h-12 text-slate-500 mx-auto mb-3" />
                      <p className="text-slate-300 mb-1">Click to upload your document</p>
                      <p className="text-slate-500 text-sm">PDF, Word, Excel, PowerPoint, Images</p>
                    </>
                  )}
                </label>
              </div>
            </div>

            {/* Print Options */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Number of Pages
                </label>
                <div className="flex items-center">
                  <button
                    type="button"
                    onClick={() => setPages(Math.max(1, pages - 1))}
                    className="w-12 h-12 bg-slate-700 hover:bg-slate-600 text-white rounded-l-lg flex items-center justify-center text-2xl font-bold transition-colors border border-slate-600"
                    data-testid="pages-minus"
                  >
                    -
                  </button>
                  <input
                    type="number"
                    min="1"
                    max="500"
                    className="w-full h-12 bg-slate-800 border-y border-slate-600 text-white text-center text-lg font-semibold focus:outline-none focus:bg-slate-700"
                    value={pages}
                    onChange={(e) => setPages(Math.max(1, parseInt(e.target.value) || 1))}
                    data-testid="pages-input"
                  />
                  <button
                    type="button"
                    onClick={() => setPages(Math.min(500, pages + 1))}
                    className="w-12 h-12 bg-slate-700 hover:bg-slate-600 text-white rounded-r-lg flex items-center justify-center text-2xl font-bold transition-colors border border-slate-600"
                    data-testid="pages-plus"
                  >
                    +
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Number of Copies
                </label>
                <div className="flex items-center">
                  <button
                    type="button"
                    onClick={() => setCopies(Math.max(1, copies - 1))}
                    className="w-12 h-12 bg-slate-700 hover:bg-slate-600 text-white rounded-l-lg flex items-center justify-center text-2xl font-bold transition-colors border border-slate-600"
                    data-testid="copies-minus"
                  >
                    -
                  </button>
                  <input
                    type="number"
                    min="1"
                    max="100"
                    className="w-full h-12 bg-slate-800 border-y border-slate-600 text-white text-center text-lg font-semibold focus:outline-none focus:bg-slate-700"
                    value={copies}
                    onChange={(e) => setCopies(Math.max(1, parseInt(e.target.value) || 1))}
                    data-testid="copies-input"
                  />
                  <button
                    type="button"
                    onClick={() => setCopies(Math.min(100, copies + 1))}
                    className="w-12 h-12 bg-slate-700 hover:bg-slate-600 text-white rounded-r-lg flex items-center justify-center text-2xl font-bold transition-colors border border-slate-600"
                    data-testid="copies-plus"
                  >
                    +
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Colour Mode
                </label>
                <select
                  className="input"
                  value={colorMode}
                  onChange={(e) => setColorMode(e.target.value)}
                  data-testid="color-mode-select"
                >
                  <option value="bw">Black & White</option>
                  <option value="color">Colour</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Printing Sides
                </label>
                <select
                  className="input"
                  value={sides}
                  onChange={(e) => setSides(e.target.value)}
                  data-testid="sides-select"
                >
                  <option value="single">Single-sided</option>
                  <option value="double">Double-sided</option>
                </select>
              </div>
            </div>

            {/* Live Price Calculator */}
            <div className="mb-6 p-4 bg-slate-800/50 border border-slate-700 rounded-xl">
              <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                <Printer className="w-4 h-4 text-blue-400" />
                Price Calculator
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                <div>
                  <p className="text-slate-400 text-xs mb-1">{sides === "double" ? "Pages → Sheets" : "Pages × Copies"}</p>
                  <p className="text-white font-bold">
                    {sides === "double" 
                      ? `${pages} → ${sheetsPerCopy} × ${copies} = ${totalSheets}` 
                      : `${pages} × ${copies} = ${totalSheets}`}
                  </p>
                </div>
                <div>
                  <p className="text-slate-400 text-xs mb-1">Price per Sheet</p>
                  <p className="text-white font-bold">{pricePerSheet}p</p>
                </div>
                <div>
                  <p className="text-slate-400 text-xs mb-1">Printing Cost</p>
                  <p className="text-white font-bold">£{(printingTotal / 100).toFixed(2)}</p>
                </div>
                <div>
                  <p className="text-slate-400 text-xs mb-1">{deliveryOption === "pickup" ? "Pick Up" : "Delivery"}</p>
                  <p className="text-white font-bold">{deliveryOption === "pickup" ? "Free" : "£2.00"}</p>
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-slate-700 flex justify-between items-center">
                <span className="text-slate-300">Total Price:</span>
                <span className="text-2xl font-bold text-blue-400">£{totalPrice}</span>
              </div>
            </div>

            {/* Delivery Option */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-slate-300 mb-3">
                Collection Method
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <label 
                  className={`card p-4 cursor-pointer border-2 transition-all ${
                    deliveryOption === "pickup" 
                      ? "border-blue-500 bg-blue-500/10" 
                      : "border-slate-700 hover:border-slate-600"
                  }`}
                  data-testid="pickup-option"
                >
                  <input
                    type="radio"
                    name="delivery"
                    value="pickup"
                    checked={deliveryOption === "pickup"}
                    onChange={(e) => setDeliveryOption(e.target.value)}
                    className="hidden"
                  />
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-white font-semibold">Pick Up</p>
                      <p className="text-slate-400 text-sm">Collect from our location</p>
                    </div>
                    <span className="text-green-400 font-bold">FREE</span>
                  </div>
                </label>
                <label 
                  className={`card p-4 cursor-pointer border-2 transition-all ${
                    deliveryOption === "delivery" 
                      ? "border-blue-500 bg-blue-500/10" 
                      : "border-slate-700 hover:border-slate-600"
                  }`}
                  data-testid="delivery-option"
                >
                  <input
                    type="radio"
                    name="delivery"
                    value="delivery"
                    checked={deliveryOption === "delivery"}
                    onChange={(e) => setDeliveryOption(e.target.value)}
                    className="hidden"
                  />
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-white font-semibold">Delivery</p>
                      <p className="text-slate-400 text-sm">We deliver to your address</p>
                    </div>
                    <span className="text-blue-400 font-bold">+£2</span>
                  </div>
                </label>
              </div>
              
              {/* Delivery Address */}
              {deliveryOption === "delivery" && (
                <div className="mt-4">
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Delivery Address *
                  </label>
                  <textarea
                    required
                    className="input resize-none"
                    rows={3}
                    placeholder="Enter your full delivery address..."
                    value={deliveryAddress}
                    onChange={(e) => setDeliveryAddress(e.target.value)}
                    data-testid="delivery-address"
                  />
                </div>
              )}
            </div>

            {/* Price Summary */}
            <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4 mb-6">
              <div className="space-y-2">
                <div className="flex justify-between text-slate-400">
                  <span>Printing ({totalSheets} {totalSheets === 1 ? 'sheet' : 'sheets'} × {pricePerSheet}p)</span>
                  <span>£{(printingTotal / 100).toFixed(2)}</span>
                </div>
                {deliveryOption === "delivery" && (
                  <div className="flex justify-between text-slate-400">
                    <span>Delivery</span>
                    <span>£2.00</span>
                  </div>
                )}
                <div className="border-t border-slate-700 pt-2 flex justify-between">
                  <span className="text-white font-semibold">Total</span>
                  <span className="text-2xl font-bold text-blue-400">£{totalPrice}</span>
                </div>
              </div>
            </div>

            {/* Contact Details */}
            <h3 className="text-lg font-semibold text-white mb-4">Your Details</h3>
            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Name *
                </label>
                <input
                  type="text"
                  required
                  className="input"
                  placeholder="Your name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  data-testid="print-name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Email *
                </label>
                <input
                  type="email"
                  required
                  className="input"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="print-email"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Phone (Optional)
                </label>
                <input
                  type="tel"
                  className="input"
                  placeholder="Your phone number"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  data-testid="print-phone"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Special Instructions (Optional)
                </label>
                <textarea
                  className="input resize-none"
                  rows={3}
                  placeholder="Any special requirements..."
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  data-testid="print-notes"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={submitting || !file}
              className="btn-primary w-full justify-center text-lg py-4"
              data-testid="print-submit"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <Printer className="w-5 h-5" />
                  Submit Print Order - £{totalPrice}
                </>
              )}
            </button>

            <p className="text-slate-500 text-sm text-center mt-4">
              We'll contact you to confirm your order and arrange payment/collection
            </p>
          </form>
        </div>
      </section>
    </MainLayout>
  );
};


export default PrintingServicesPage;
